import React from 'react'
import ReactDOM from 'react-dom'
import { FormikProps } from 'formik'
import { IntlShape } from 'react-intl'
import { TourDetails, TourEvent, images } from '../../../core/_models'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import ImageUploader from '../../../../../../../_dashboard/partials/widgets/upload/ImageUploader'
import { ID } from '../../../../../../../_dashboard/helpers'

interface ItineraryModalProps {
  show: boolean
  onHide: () => void
  formik: FormikProps<TourDetails>
  newItinerary: TourEvent
  setNewItinerary: React.Dispatch<React.SetStateAction<TourEvent>>
  handleAddItinerary: (event: TourEvent) => void
  intl: IntlShape
  events: TourEvent[]
}

export const ItineraryModal: React.FC<ItineraryModalProps> = ({
  show,
  onHide,
  formik,
  newItinerary,
  setNewItinerary,
  handleAddItinerary,
  intl,
  events
}) => {
  if (!show) return null

  const handleImageAdded = (newImage: { url: string; _id: string }) => {
    // Store both ID and URL for display purposes
    setNewItinerary((prev) => ({
      ...prev,
      image: {
        _id: newImage._id as unknown as ID,
        url: newImage.url,
        seoUrl: ''
      }
    }))
  }

  return ReactDOM.createPortal(
    <>
      <div
        className='modal fade show d-block'
        tabIndex={-1}
        role='dialog'
        style={{zIndex: 1050, backgroundColor: 'rgba(15, 23, 42, 0.45)'}}
      >
        <div className='modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg'>
          <div className='modal-content border-0 rounded-4 shadow-lg overflow-hidden'>
            <div className='modal-header border-0 pt-6 px-8 pb-2'>
              <h5 className='modal-title fs-3 fw-bold text-gray-900'>
                {newItinerary._id 
                  ? intl.formatMessage({ id: 'TOUR.ACTIONS.EDIT_ELEMENT' })
                  : intl.formatMessage({ id: 'TOUR.ACTIONS.ADD_ELEMENT' })}
              </h5>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-active-color-primary'
                onClick={onHide}
                aria-label='Close'
              >
                <i className='bi bi-x-lg fs-3' />
              </button>
            </div>
            <div className='modal-body px-8 py-6'>
              <div className='form-group mb-7'>
                <label className='form-label required mb-3'>
                  {intl.formatMessage({ id: 'TOUR.FIELDS.ITINERARY_TITLE' })}
                </label>
                <input
                  type='text'
                  className='form-control form-control-solid'
                  placeholder={intl.formatMessage({ id: 'TOUR.FIELDS.ENTER_TITLE' })}
                  value={newItinerary.title}
                  onChange={(e) =>
                    setNewItinerary((prev) => ({ ...prev, title: e.target.value }))
                  }
                />
              </div>
              <div className='form-group mb-7'>
                <label className='form-label required mb-3'>
                  {intl.formatMessage({ id: 'TOUR.FIELDS.ITINERARY_CONTENT' })}
                </label>
                <div className="quill-container" style={{ height: '200px', marginBottom: '60px' }}>
                  <ReactQuill
                    value={newItinerary.content || ''}
                    onChange={(value) => setNewItinerary((prev) => ({ ...prev, content: value }))}
                    modules={{
                      toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        ['blockquote'],
                        [{ header: 1 }, { header: 2 }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ indent: '-1' }, { indent: '+1' }],
                        [{ direction: 'rtl' }],
                        [{ size: ['small', false, 'large', 'huge'] }],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        [{ font: [] }],
                        ['link'],
                        ['clean'],
                      ],
                    }}
                    formats={[
                      'header',
                      'font',
                      'size',
                      'bold',
                      'italic',
                      'underline',
                      'strike',
                      'blockquote',
                      'list',
                      'bullet',
                      'indent',
                      'link',
                      'image',
                      'video',
                      'direction',
                    ]}
                    style={{ height: '170px' }}
                  />
                </div>
              </div>
              <div className='form-group mb-7'>
                <label className='form-label mb-3'>
                  {intl.formatMessage({ id: 'TOUR.FIELDS.DESCRIPTION' })}
                </label>
                <div className="quill-container" style={{ height: '200px', marginBottom: '60px' }}>
                  <ReactQuill
                    value={newItinerary.description || ''}
                    onChange={(value) => setNewItinerary((prev) => ({ ...prev, description: value }))}
                    modules={{
                      toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        ['blockquote'],
                        [{ header: 1 }, { header: 2 }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ indent: '-1' }, { indent: '+1' }],
                        [{ direction: 'rtl' }],
                        [{ size: ['small', false, 'large', 'huge'] }],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        [{ font: [] }],
                        ['link'],
                        ['clean'],
                      ],
                    }}
                    formats={[
                      'header',
                      'font',
                      'size',
                      'bold',
                      'italic',
                      'underline',
                      'strike',
                      'blockquote',
                      'list',
                      'bullet',
                      'indent',
                      'link',
                      'image',
                      'video',
                      'direction',
                    ]}
                    style={{ height: '170px' }}
                  />
                </div>
              </div>
              <div className='form-group mb-7'>
                <label className='form-label mb-3'>
                  {intl.formatMessage({ id: 'TOUR.FIELDS.IMAGE' })}
                </label>
                <div className='gallery gallery-style'>
                  {newItinerary.image && (
                    <div className='d-flex flex-row justify-content-space-between align-items-start'>
                      <img
                        src={typeof newItinerary.image === 'string' 
                          ? (newItinerary.image.includes('/images/') 
                              ? (process.env.REACT_APP_API_URL_IMAGE || '') + newItinerary.image 
                              : newItinerary.image)
                          : (newItinerary.image.url.includes('/images/') 
                              ? (process.env.REACT_APP_API_URL_IMAGE || '') + newItinerary.image.url 
                              : newItinerary.image.url)}
                        alt="Event"
                        className='border-image h-175px'
                        style={{ maxWidth: '200px', objectFit: 'cover' }}
                      />
                      <button
                        type='button'
                        className='btn btn-icon btn-sm btn-light-danger ms-2'
                        onClick={() => setNewItinerary((prev) => ({ ...prev, image: undefined }))}
                        title="Remove image"
                      >
                        <i className='fas fa-times'></i>
                      </button>
                    </div>
                  )}
                  {!newItinerary.image && (
                    <ImageUploader
                      onImageUpload={(file) => console.log('Image uploaded:', file)}
                      onImageAdded={handleImageAdded}
                      uploadEndpoint='/images'
                      tourId={formik.values.id?.toString()}
                      placement='tour_itinerary'
                    />
                  )}
                </div>
              </div>
              <div className='row mb-7'>
                <div className='col-md-3'>
                  <div className='form-group'>
                    <label className='form-label'>
                      {intl.formatMessage({ id: 'TOUR.FIELDS.DAY' })}
                    </label>
                    <input
                      type='number'
                      min="1"
                      className='form-control form-control-solid'
                      placeholder={intl.formatMessage({ id: 'TOUR.FIELDS.ENTER_DAY' })}
                      value={newItinerary.day}
                      onChange={(e) =>
                        setNewItinerary((prev) => ({ ...prev, day: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='form-group'>
                    <label className='form-label'>
                      {intl.formatMessage({ id: 'TOUR.FIELDS.START_TIME' })}
                    </label>
                    <input
                      type='time'
                      className='form-control form-control-solid'
                      value={newItinerary.startTime}
                      onChange={(e) =>
                        setNewItinerary((prev) => ({ ...prev, startTime: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='form-group'>
                    <label className='form-label'>
                      {intl.formatMessage({ id: 'TOUR.FIELDS.END_TIME' })}
                    </label>
                    <input
                      type='time'
                      className='form-control form-control-solid'
                      value={newItinerary.endTime}
                      onChange={(e) =>
                        setNewItinerary((prev) => ({ ...prev, endTime: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='form-group'>
                    <label className='form-label'>Order</label>
                    <input
                      type='number'
                      min="1"
                      className='form-control form-control-solid'
                      value={newItinerary.order}
                      onChange={(e) =>
                        setNewItinerary((prev) => ({ ...prev, order: parseInt(e.target.value) }))
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='modal-footer border-0 pt-0 pb-7 px-8 d-flex justify-content-end gap-3'>
              <button
                type='button'
                className='btn btn-light-primary fw-semibold px-6'
                onClick={() => {
                  onHide()
                  setNewItinerary({
                    title: '',
                    content: '',
                    description: '',
                    startTime: '',
                    endTime: '',
                    day: '',
                    order: (events?.length || 0) + 1,
                    image: undefined
                  } as TourEvent)
                }}
              >
                {intl.formatMessage({ id: 'TOUR.ACTIONS.CANCEL' })}
              </button>
              <button
                type='button'
                className='btn btn-primary fw-semibold px-8'
                onClick={() => handleAddItinerary(newItinerary)}
              >
                {intl.formatMessage({ id: 'TOUR.ACTIONS.SAVE' })}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show' style={{zIndex: 1040, backgroundColor: 'rgba(15, 23, 42, 0.45)'}}></div>
    </>,
    document.body
  )
} 